<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-card flat>
    <v-card-title class="title font-weigth-light px-0 py-0 mb-4">
      Utilisation Report
    </v-card-title>
    <v-card-text class="px-0">
      <v-row class="mb-0">
        <v-col cols="3">
          <date-picker
            v-model="datePicker.start"
            :masks="datePicker.masks"
            :min-date="datePicker.minimum"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="From Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'Start Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <date-picker
            v-model="datePicker.end"
            :masks="datePicker.masks"
            :min-date="datePicker.start"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="To Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'End Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedAssetGroups"
            :items="assetGroups"
            item-text="name"
            item-value="id"
            label="Asset Group"
            multiple
            outlined
            required
            @change="changeAssetGroup; checkAllAssetGroup(false)"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllAssetGroup(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllAssetGroup }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllAssetGroup }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedAssetGroups.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedAssetGroups.length - 1 }} Others)
              </span>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedVehicle"
            :items="filteredVehicles"
            item-text="device_name"
            item-value="id"
            label="Vehicle"
            multiple
            outlined
            required
            @change="(items) => { changeVehicle(items); checkAllVehicle(false) }"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllVehicle(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllVehicle }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllVehicle }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.device_name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedVehicle.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedVehicle.length - 1 }} Others)&nbsp;
              </span>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0"
        >
          <h3>
            Advanced Options
            <v-icon
              class="ml-2"
              :title="showOptions ? 'Hide Options' : 'Show Options'"
              @click="showOptions = !showOptions"
            >
              {{ showOptions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </h3>
          <div
            v-if="showOptions"
            class="mt-4 border border-primary rounded p-3"
          >
            <h4>Working Days</h4>
            <v-row>
              <v-col
                v-for="(day, index) in ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                :key="index"
                cols="1"
                class="mt-0"
              >
                <v-checkbox
                  class="mt-0"
                  v-model="workingDays[index]"
                  :label="day"
                  color="primary"
                  hide-details
                />
              </v-col>
            </v-row>
            <h4 class="mt-2">
              Working Hours
            </h4>
            <v-row class="mt-2">
              <v-col
                v-for="(hour, index) in Object.keys(workingHours)"
                :key="index"
                cols="2"
                class="mt-0"
              >
                <v-text-field
                  v-model="workingHours[hour]"
                  :label="hour.charAt(0).toUpperCase() + hour.slice(1)"
                  type="time"
                  outlined
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-end"
        >
          <v-btn
            border
            color="success"
            @click="downloadReport"
            class="mr-2"
            :disabled="isLoading || isDownload || dataPerTab.length === 0"
          >
            {{ isDownload ? 'Downloading' : 'Download' }}
          </v-btn>
          <v-btn
            border
            color="primary"
            @click="generateReport"
          >
            {{ isLoading ? 'Generating' : 'Generate' }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && selectedTabForDisplay.length">
        <v-col cols="12">
          <v-tabs
            bg-color="primary"
            v-model="selectedTab"
          >
            <v-icon
              :title="isJoin ? 'Expand' : 'Collapse'"
              @click="handleJoin"
            >
              {{ isJoin ? 'mdi-arrow-expand' : 'mdi-arrow-collapse' }}
            </v-icon>
            <v-tab
              ref="tab"
              v-for="tab in selectedTabForDisplay"
              :key="tab.id"
              :value="tab.id"
              @click="generateReport(tab.id)"
            >
              {{ tab.device_name }}
            </v-tab>
          </v-tabs>
          <v-data-table
            :headers="headers"
            :items="dataPerTab"
            class="font-weight-light pa-5"
            :loading="isLoading"
            loading-text="Loading"
            :footer-props="footerProps"
            :options.sync="paginationOptions"
          >
            <template #[`item.utilisation_percentage`]="{ item }">
              <v-progress-linear
                v-model="item.utilisation_percentage"
                height="20"
                :color="item.utilisation_percentage > 70 ? 'green' : 'yellow'"
              >
                <strong>{{ item.utilisation_percentage }}%</strong>
              </v-progress-linear>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isLoading && selectedTabForDisplay.length === 0">
        <v-col cols="12">
          <p class="text-center">
            Loading
          </p>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && !isLoading && selectedTabForDisplay.length === 0">
        <v-col cols="12">
          <p class="text-center">
            No Data
          </p>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import momentTz from "moment-timezone";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import get from "lodash/get";

export default {
  data() {
    return {
      showOptions: false,
      assetGroups: [],
      vehicles: [],
      assetTypes: [],
      geofences: [],
      selectedVehicle: [],
      selectedAssetGroups: [],
      selectedVehicleForTabs: [],
      datePicker: {
        start: '',
        startUnix: '',
        paramStart: '',
        end: '',
        endUnix: '',
        paramEnd: '',
        masks: {
          input: 'DD MMMM YYYY'
        },
      },
      selectedTab: '',
      headers: [
        { text: "Vehicle", value: "vehicle" },
        { text: "Identifier", value: "identifier" },
        { text: "Licence Plate", value: "licence_plate" },
        { text: "Branch", value: "branch" },
        { text: "Utilisation Percentage", value: "utilisation_percentage" },
        { text: "Hours Used", value: "hours_used" },
        { text: "Hours Unused", value: "hours_unused" },
        { text: "No. of Bookings", value: "books" },
      ],
      footerProps: {
        itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
      },
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      data: [],
      isLoading: false,
      isGenerate: false,
      isDownload: false,
      timeout: 0,
      textCheckAllAssetGroup: 'CHECK ALL ASSET GROUP',
      iconCheckAllAssetGroup: 'mdi-check-all',
      textCheckAllVehicle: 'CHECK ALL VEHICLE',
      iconCheckAllVehicle: 'mdi-check-all',
      isJoin: true,
      workOrders: [],
      workingDays: [0, 1, 1, 1, 1, 1, 0],
      workingHours: {
        start: '08:00',
        end: '17:00'
      }
    };
  },
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({
        vehicles: [],
        assetGroups: [],
        assetTypes: [],
        geofences: [],
      }),
    },
  },
  created() {
    if (this.isWSP) {
      this.getDataWorkOrder();
      this.$store.dispatch("LOAD_TIMEZONES");
    }
  },
  mounted() {
    this.vehicles = this.params.vehicles;
    this.assetTypes = this.params.assetTypes;
    this.assetGroups = this.params.assetGroups.filter((group) => {
      const assetType = this.params.assetTypes.find((type) => type.id === group.metadata.asset_type);
      return assetType && assetType.is_vehicle == '1';
    });
    this.geofences = this.params.geofences;
  },
  watch: {
    "datePicker.start"(value) {
      const startDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramStart = startDate;
    },
    "datePicker.end"(value) {
      const endDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramEnd = endDate;
    },
  },
  computed: {
    user() {
      const { user = {} } = this.$store.getters["getUserData"];
      return user;
    },
    timezones() {
      return this.$store.getters["getTimezones"];
    },
    userIanaTimezone() {
      if (this.user.timezone) {
        const timezone = this.timezones.find(
          tz => tz.name === this.user.timezone
        );
        if (timezone) {
          return timezone.type;
        }
      }
      return 'Australia/Sydney';
    },
    timezoneDate() {
      const currentTimezone = this.timezones.find(
        timezone => timezone.name === this.user.timezone
      );
      if (!currentTimezone) return "";
      const currentDate = momentTz
        .tz(currentTimezone.type)
        .format("YYYY-MM-DD HH:mm:ss");
      return currentDate;
    },
    dataPerTab() {
      const vehicle = this.selectedTabForDisplay[this.selectedTab];
      if (!vehicle) return [];

      let dataFilter = this.data;
      if (vehicle.id !== '__JOIN__') {
        dataFilter = this.data.filter(
          (item) => item.book_detail.vehicle_id === vehicle.id
        );
      }

      const tz = this.userIanaTimezone || 'Australia/Sydney';
      const mtz = (s) => moment.tz(s, tz);
  const reportStart = mtz(this.datePicker.paramStart).startOf('day');
  const reportEnd   = mtz(this.datePicker.paramEnd).endOf('day');
  const nowTz       = moment.tz(tz);
  const effectiveEnd= moment.min(reportEnd, nowTz);

  const { whStart, whEnd, hasWindow: hasWorkWindow } = this.getNormalizedWorkingWindow();
  const wdays = this.getNormalizedWorkingDays();

      const values = dataFilter
        .sort((a, b) => {
          const aStart = mtz(a.book_detail.start_datetime);
          const bStart = mtz(b.book_detail.start_datetime);
          return aStart.diff(bStart);
        })
        .reduce((obj, value) => {
          try { value.book_detail.vehicle_metadata = JSON.parse(value.book_detail.vehicle_metadata); } catch (e) {}

          const vid = value.book_detail.vehicle_id;

          if (!obj.hasOwnProperty(vid)) {
            const meta = value.book_detail.vehicle_metadata || {};
            obj[vid] = {
              vehicle: (meta.device_plate || '') + ' ' + value.book_detail.vehicle_name,
              identifier: value.book_detail.vehicle_name,
              licence_plate: meta.device_plate || '',
              branch: this.getOriginName(value.book_detail),
              hours_used: 0,
              hours_unused: 0,
              books: 0,
              _intervals: [],
            };
          }

          if (!hasWorkWindow) return obj;

          const bookingStart = mtz(value.book_detail.start_datetime);
          const bookingEnd   = mtz(value.book_detail.end_datetime);

          const overlapsReport = bookingStart.isBefore(effectiveEnd) && bookingEnd.isAfter(reportStart);
          if (overlapsReport) obj[vid].books += 1;

          const clipped = this.clampIntervalToRange(
            bookingStart,
            bookingEnd,
            reportStart,
            effectiveEnd
          );
          if (!clipped) return obj;

          const [clipStart, clipEnd] = clipped;

          let dayIter = clipStart.clone().startOf('day');
          const lastDay = clipEnd.clone().startOf('day');

          while (dayIter.isSameOrBefore(lastDay, 'day')) {
            const dow = dayIter.day();
            if (wdays[dow]) {
              const workStart = mtz(dayIter.format('YYYY-MM-DD') + ' ' + whStart);
              const workEnd   = mtz(dayIter.format('YYYY-MM-DD') + ' ' + whEnd);

              const intersect = this.clampIntervalToRange(clipStart, clipEnd, workStart, workEnd);
              if (intersect) {
                obj[vid]._intervals.push(intersect);
              }
            }
            dayIter.add(1, 'day');
          }

          return obj;
        }, {});

  const baseHours = this.getTotalWorkingHoursRange();

      return Object.values(values).map((item) => {
        const merged = this.mergeIntervals(item._intervals || []);
        let used = this.sumIntervals(merged);
        used = Math.min(used, baseHours);
        const unused = Math.max(0, baseHours - used);
        const util = baseHours > 0 ? (used / baseHours) * 100 : 0;
        item.hours_used = used.toFixed(2);
        item.hours_unused = unused.toFixed(2);
        item.utilisation_percentage = util.toFixed(2);
  // debug trace removed
        delete item._intervals;
        return item;
      });
    },
    filteredVehicles() {
      if (this.selectedAssetGroups.length) {
        return this.vehicles.filter(
          (vehicle) => this.selectedAssetGroups.some(
            (group) => {
              return group.members && group.members.find((member) => member.detail.id === vehicle.id);
            }
          )
        );
      }
      return this.vehicles;
    },
    selectedTabForDisplay() {
      if (this.isJoin) {
        return [{
          id: '__JOIN__',
          device_name: 'All'
        }]
      }
      return this.selectedVehicleForTabs;
    },
    isWSP() {
      return this.$store.state.clientid == 'H1O5G7P0N3JA';
    },
    isIntimap() {
      return this.$store.state.clientid == 'DSSTWWLLIHH7';
    },
  },
  methods: {
    clampIntervalToRange(start, end, rangeStart, rangeEnd) {
      const s = moment.max(start, rangeStart);
      const e = moment.min(end, rangeEnd);
      if (e.isAfter(s)) return [s, e];
      return null;
    },
    mergeIntervals(intervals) {
      if (!intervals || intervals.length === 0) return [];
      const sorted = intervals
        .map(([s, e]) => [s.clone(), e.clone()])
        .sort((a, b) => a[0].valueOf() - b[0].valueOf());

      const merged = [];
      let [curS, curE] = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        const [s, e] = sorted[i];
        if (s.isSameOrBefore(curE)) {
          if (e.isAfter(curE)) curE = e;
        } else {
          merged.push([curS, curE]);
          [curS, curE] = [s, e];
        }
      }
      merged.push([curS, curE]);
      return merged;
    },
    sumIntervals(intervals) {
      return intervals.reduce((sum, [s, e]) => sum + moment.duration(e.diff(s)).asHours(), 0);
    },
  getTotalWorkingHoursRange() {
      const tz  = this.userIanaTimezone || 'Australia/Sydney';
      const mtz = (s) => moment.tz(s, tz);

  const rangeStart = this.datePicker.paramStart;
  const rangeEnd = this.datePicker.paramEnd;
  const nowTz = moment.tz(tz);
      
  const { whStart, whEnd } = this.getNormalizedWorkingWindow();
  const wdays = this.getNormalizedWorkingDays();

      if (whStart === whEnd) return 0;

  let total = 0;
  let day   = mtz(rangeStart).clone().startOf('day');
  const reportLastDay = mtz(rangeEnd).clone().startOf('day');
  const lastDay = moment.min(reportLastDay, nowTz.clone().startOf('day'));

      while (day.isSameOrBefore(lastDay, 'day')) {
  const dow = day.day();
  if (wdays[dow]) {
          let ws = mtz(day.format('YYYY-MM-DD') + ' ' + whStart);
          let we = mtz(day.format('YYYY-MM-DD') + ' ' + whEnd);
          // Cap today's working window at now
          if (day.isSame(nowTz, 'day')) {
            if (nowTz.isBefore(we)) we = nowTz.clone();
          }

          if (we.isAfter(ws)) {
            total += moment.duration(we.diff(ws)).asHours();
          }
        }
        day.add(1, 'day');
      }

      return +total.toFixed(6);
    },
    // Normalize workingDays to booleans; default to Mon-Fri if all off
    getNormalizedWorkingDays() {
      const src = Array.isArray(this.workingDays) ? this.workingDays : [];
      const norm = new Array(7).fill(false);
      for (let i = 0; i < 7; i++) {
        const v = src[i];
        norm[i] = v === true || v === 1 || v === '1';
      }
      if (!norm.some(Boolean)) {
        norm[1] = norm[2] = norm[3] = norm[4] = norm[5] = true;
      }
      return norm;
    },
    // Ensure working hours are valid HH:mm; fallback to defaults
    getNormalizedWorkingWindow() {
      const defStart = '08:00';
      const defEnd = '17:00';
      let s = (this.workingHours && this.workingHours.start) ? String(this.workingHours.start).trim() : defStart;
      let e = (this.workingHours && this.workingHours.end)   ? String(this.workingHours.end).trim()   : defEnd;

      const coerce = (val, def) => {
        // Accept HH:mm, HH, or H; coerce to HH:00
        if (/^\d{1,2}:\d{2}$/.test(val)) return val;
        if (/^\d{1,2}$/.test(val)) return `${val.padStart(2,'0')}:00`;
        return def;
      };

      s = coerce(s, defStart);
      e = coerce(e, defEnd);

      const hasWindow = s !== e;
      return { whStart: s, whEnd: e, hasWindow };
    },
    getDataWorkOrder() {
      this.$store
        .dispatch("API_CALL", {
          $method: "get_work_order",
          $postData: {
            clientid: this.$store.state.clientid
          }
        })
        .then(response => {
          this.workOrders = response.data.data;
        });
    },
    handleJoin() {
      this.isJoin = !this.isJoin;
      this.selectedTab = 0;
      // Expanding is the only mode that renders per-vehicle tabs, so this is
      // where the tab list is fetched. See generateReport for why.
      if (this.isJoin) {
        this.$nextTick(() => {
          if (this.$refs.tab && this.$refs.tab[0]) this.$refs.tab[0].$el.click();
        });
        return;
      }
      // Hold the loading state across the fetch, otherwise the empty tab list
      // flashes "No Data" before the tabs arrive.
      this.isLoading = true;
      this.getDataBookTabs().then(() => {
        this.$nextTick(() => {
          if (this.$refs.tab && this.$refs.tab[0]) {
            // The tab click re-enters generateReport, which owns isLoading from here.
            this.$refs.tab[0].$el.click();
          } else {
            this.isLoading = false;
          }
        });
      });
    },
    checkAllAssetGroup(force = false) {
      if (force) {
        if (this.textCheckAllAssetGroup === 'CHECK ALL ASSET GROUP') {
          this.selectedAssetGroups = this.assetGroups;
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.selectedAssetGroups = [];
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      } else {
        if (this.selectedAssetGroups.length === this.assetGroups.length) {
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      }
    },
    changeAssetGroup() {
      this.selectedAssetGroups = this.selectedAssetGroups.filter((assetGroup) => typeof assetGroup === 'object' && assetGroup.id);
    },
    checkAllVehicle(force = false) {
      if (force) {
        if (this.textCheckAllVehicle === 'CHECK ALL VEHICLE') {
          this.selectedVehicle = this.filteredVehicles;
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.selectedVehicle = [];
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      } else {
        if (this.selectedVehicle.length === this.filteredVehicles.length) {
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      }
    },
    changeVehicle(items) {
      this.selectedVehicle = items.filter((vehicle) => typeof vehicle === 'object' && vehicle.id);
    },
    generateReport(vehicleId) {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end) return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        let selectedVehicleId = null;
        if (typeof vehicleId !== 'object') {
          selectedVehicleId = vehicleId;
        }
        // Flag the load on the click itself, not inside the debounce, so the
        // button reacts immediately instead of sitting unchanged for 500ms.
        this.isLoading = true;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          // get_book_for_tabs is as expensive as get_all_book — same vehicle set,
          // same date range — but its result only feeds the per-vehicle tab list.
          // In the default joined view selectedTabForDisplay is hardcoded to a
          // single 'All' tab, so fetching it there doubled the report's cost and
          // then threw the answer away. handleJoin fetches it when expanding.
          const needsTabs = selectedVehicleId == null && !this.isJoin;
          const promise = needsTabs ? this.getDataBookTabs() : Promise.resolve();
          promise.then(() => {
            if (selectedVehicleId == null) {
              if (this.selectedTabForDisplay.length) {
                this.selectedTab = 0;
                this.getDataBook(this.selectedTabForDisplay[0].id);
              } else {
                this.data = [];
                this.isGenerate = true;
                this.isLoading = false;
              }
            } else {
              this.getDataBook(selectedVehicleId);
            }
          })
          .catch((error) => {
            // Backstop: nothing above should reject, but a stuck spinner is worse
            // than a logged error.
            console.error('Error generating utilisation report:', error);
            this.isLoading = false;
          })
        }, 500);
      }
    },
    downloadReport() {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end || this.isDownload) return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        const payload = {
          export: 1,
          report: 'utilisation',
          clientid: this.$store.state.clientid,
          start_date: `${this.datePicker.paramStart} ${this.workingHours.start}:${this.workingHours.start.slice(-2) == '00' ? '00' : '59'}`,
          end_date: `${this.datePicker.paramEnd} ${this.workingHours.end}:${this.workingHours.end.slice(-2) == '00' ? '00' : '59'}`,
          working_days: this.workingDays,
          vehicle_id: this.selectedVehicle.map((vehicle) => vehicle.id),
          get_schedules: 1,
          get_recurring: 0,
        };
        if (this.isIntimap || this.isWSP) {
          payload.use_timezone_depot = 1;
        }
        this.isDownload = true;
        this.$store.dispatch('API_CALL', {
          $method: 'get_all_book',
          $postData: payload,
        })
        .then((response) => {
          const payloadData = get(response, 'data.data', null);
          if (payloadData && Object.prototype.hasOwnProperty.call(payloadData, 'file')) {
            const lastFileName = payloadData.file.split('/').pop();
            const url = `${process.env.VUE_APP_CARPOOLING_URL}/../assets/uploads/carpooling/${lastFileName}`;
            fetch(url)
              .then(response => {
                if (!response.ok) {
                  throw new Error('Network response was not ok');
                }
                return response.blob();
              })
              .then(blob => {
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = lastFileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(downloadUrl);
              })
              .catch(error => {
                console.error('There has been a problem with your fetch operation:', error);
              });
          }
          this.isDownload = false;
        })
        .catch((error) => {
          // Without this the button stays disabled on "Downloading" for good.
          console.error('Error exporting utilisation report:', error);
          this.isDownload = false;
        });
      }
    },
    getDataBookTabs() {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_book_for_tabs',
        $postData: {
          vehicle_id: this.selectedVehicle.map((vehicle) => vehicle.id),
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.selectedVehicleForTabs = response.data.data.map((vehicle) => this.selectedVehicle.find((v) => v.id === vehicle.vehicle_id));
      })
      .catch((error) => {
        // Resolve rather than reject: callers continue with an empty tab list and
        // clear their own loading state, instead of the chain stalling on
        // "Generating" forever.
        console.error('Error loading utilisation tab list:', error);
        this.selectedVehicleForTabs = [];
      });
    },
    getDataBook(vehicleId) {
      const postData = {
        clientid: this.$store.state.clientid,
        start_date: `${this.datePicker.paramStart} ${this.workingHours.start}:${this.workingHours.start.slice(-2) == '00' ? '00' : '59'}`,
        end_date: `${this.datePicker.paramEnd} ${this.workingHours.end}:${this.workingHours.end.slice(-2) == '00' ? '00' : '59'}`,
        get_schedules: 1,
        get_recurring: 0,
      }
      if (vehicleId != '__JOIN__') {
        postData.vehicle_id = vehicleId;
      } else {
        postData.vehicle_id = this.selectedVehicle.map((vehicle) => vehicle.id);
      }
      this.isLoading = true;
      if (this.isIntimap || this.isWSP) {
        postData.use_timezone_depot = 1;
      }
      return this.$store.dispatch('API_CALL', {
        $method: 'get_all_book',
        $postData: postData,
      })
      .then((response) => {
        const rows = get(response, 'data.data', []);
        this.data = (Array.isArray(rows) ? rows : []).map(item => {
          if (!item.book_detail.vehicle_name) {
            // A booking can reference a vehicle that is no longer in the list;
            // reading .device_name off the undefined match used to throw here and
            // leave the button stuck on "Generating".
            const vehicle = this.vehicles.find((v) => v.id === item.book_detail.vehicle_id);
            item.book_detail.vehicle_name = vehicle ? vehicle.device_name : '';
          }
          try {
            item.book_detail.metadata = JSON.parse(item.book_detail.metadata);
          } catch (e) {}
          return item;
        });
        this.isGenerate = true;
        this.isLoading = false;
      })
      .catch((error) => {
        console.error('Error loading utilisation report:', error);
        this.data = [];
        this.isGenerate = true;
        this.isLoading = false;
      });
    },
    getOriginName(item) {
      let destination = '';
      if (item.start_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.start_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
  },
};
</script>
